/**
 * 2D affine transforms in the `[a, b, c, d, e, f]` form PSD's `Trnf` and CSS `matrix()` share:
 * `(x, y) → (ax + cy + e, bx + dy + f)`, y pointing down.
 */

export type Affine = [number, number, number, number, number, number];

export const IDENTITY: Affine = [1, 0, 0, 1, 0, 0];

/** `m` applied after `n`, i.e. the matrix for `m ∘ n`. */
export function multiply(m: Affine, n: Affine): Affine {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

export function applyPoint(
  m: Affine,
  point: { x: number; y: number },
): { x: number; y: number } {
  return {
    x: m[0] * point.x + m[2] * point.y + m[4],
    y: m[1] * point.x + m[3] * point.y + m[5],
  };
}

/**
 * A layer's box mapped into its parent's, mirroring `layer-css.ts`: the anchor translate folds
 * into `left`/`top`, then rotation, flips and uniform scale pivot on `origin` (default: box center, as in CSS).
 */
export function boxTransform(args: {
  left: number;
  top: number;
  width: number;
  height: number;
  rotation: number;
  scale?: number;
  flipX?: boolean;
  flipY?: boolean;
  origin?: { x: number; y: number };
}): Affine {
  const { left, top, width, height, rotation, flipX, flipY } = args;
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const scaleX = (args.scale ?? 1) * (flipX ? -1 : 1);
  const scaleY = (args.scale ?? 1) * (flipY ? -1 : 1);
  // rotate(θ) · scale(sx, sy) — the flips mirror in the rotated space.
  const linear: Affine = [
    cos * scaleX,
    sin * scaleX,
    -sin * scaleY,
    cos * scaleY,
    0,
    0,
  ];
  const cx = args.origin?.x ?? width / 2;
  const cy = args.origin?.y ?? height / 2;
  return multiply(
    [1, 0, 0, 1, left + cx, top + cy],
    multiply(linear, [1, 0, 0, 1, -cx, -cy]),
  );
}
